"use client";

import { useRouter } from "next/navigation";
import { twMerge } from "tailwind-merge";

import { SEARCH_PARAMS } from "@/globals";
import { messages } from "@/globals.content";
import { type RSearchFiltersType } from "@/schemes/raabits.scheme";
import { TYPO_STYLES } from "@/styles/typography";
import { WRAPPER_STYLES } from "@/styles/wrapper";

type SearchModuleDisplayProps = {
    baseURL: string;
    currentSearchString: string;
    entryId?: string;
    initialSearchString: string;
    searchFilters: RSearchFiltersType | undefined;
};

type Tag = {
    key: string;
    type: SEARCH_PARAMS;
    value: string;
};

export const SearchModuleDisplayView = ({
    searchFilters,
    initialSearchString,
    currentSearchString,
    entryId,
    baseURL,
}: SearchModuleDisplayProps) => {
    const router = useRouter();

    const currentSearchParams = new URLSearchParams(currentSearchString);
    const initialSearchParams = new URLSearchParams(initialSearchString);

    const handleRemove = (tag: Tag) => {
        currentSearchParams.delete(tag.type, tag.key);
        currentSearchParams.delete(SEARCH_PARAMS.page);
        router.push(`${baseURL}?${currentSearchParams.toString()}${entryId ? "#" + entryId : ""}`);
    };

    const handleRemoveAll = () => {
        router.push(`${baseURL}?${initialSearchParams.toString()}${entryId ? "#" + entryId : ""}`);
    };

    return (
        <div className={twMerge(WRAPPER_STYLES.HTopLeft(), "gap-4 flex-wrap")}>
            {searchFilters &&
                currentSearchParams
                    .getAll(SEARCH_PARAMS.faecher)
                    .map<Tag | undefined>(
                        (key) =>
                            (searchFilters.faecher[key] && {
                                type: SEARCH_PARAMS.faecher,
                                key,
                                value: searchFilters.faecher[key],
                            }) ||
                            undefined,
                    )
                    .concat(
                        currentSearchParams
                            .getAll(SEARCH_PARAMS.klassenstufen)
                            .map<Tag | undefined>(
                                (key) =>
                                    (searchFilters.klassenstufen[key] && {
                                        type: SEARCH_PARAMS.klassenstufen,
                                        key,
                                        value: searchFilters.klassenstufen[key],
                                    }) ||
                                    undefined,
                            ),
                    )
                    .concat(
                        currentSearchParams.getAll(SEARCH_PARAMS.schularten).map<Tag | undefined>(
                            (key) =>
                                (searchFilters.schularten[key] && {
                                    type: SEARCH_PARAMS.schularten,
                                    key,
                                    value: searchFilters.schularten[key],
                                }) ||
                                undefined,
                        ),
                    )
                    .filter(
                        (tag): tag is Tag => !!tag && !initialSearchParams.has(tag.type, tag.key),
                    )
                    .map((tag) => (
                        <button
                            key={tag.type + "_" + tag.key}
                            className="h-8 px-3 py-1 flex flex-nowrap justify-center items-center gap-2.5
                            font-light text-nowrap bg-grey3 rounded-3xl
                            hover:bg-greyOpacity10 cursor-pointer text-brandDark"
                            onClick={() => handleRemove(tag)}
                        >
                            {tag.value}
                            <span
                                className={twMerge(TYPO_STYLES.icon(), "text-2xl text-brandDark")}
                                aria-label={messages.tag.remove}
                                title={messages.search.reset}
                            >
                                {String.fromCharCode(Number("0xe91b"))}
                            </span>
                        </button>
                    ))}
            {(hasFilter(currentSearchParams, initialSearchParams) && (
                <button
                    className="h-8 px-3 py-1 flex flex-nowrap justify-center items-center gap-2.5
                            font-light text-nowrap bg-grey3 rounded-3xl
                            hover:bg-greyOpacity10 cursor-pointer text-brandDark"
                    onClick={handleRemoveAll}
                >
                    {messages.tag.removeAll}
                </button>
            )) ||
                undefined}
        </div>
    );
};

function hasFilter(params1: URLSearchParams, params2: URLSearchParams): boolean {
    return (
        hasArrayDifference(
            params1.getAll(SEARCH_PARAMS.faecher),
            params2.getAll(SEARCH_PARAMS.faecher),
        ) ||
        hasArrayDifference(
            params1.getAll(SEARCH_PARAMS.klassenstufen),
            params2.getAll(SEARCH_PARAMS.klassenstufen),
        ) ||
        hasArrayDifference(
            params1.getAll(SEARCH_PARAMS.schularten),
            params2.getAll(SEARCH_PARAMS.schularten),
        )
    );
}

function hasArrayDifference<T>(arr1: T[], arr2: T[]): boolean {
    if (arr1.length !== arr2.length) return true;

    return arr1.some((item) => !arr2.includes(item)) || arr2.some((item) => !arr1.includes(item));
}
